/**
 * Parse #project and #task mentions from a worklog description
 *
 * Format: "description text #ProjectName #TaskName"
 * Returns: {
 *   cleanDescription: "description text",
 *   projectName: "ProjectName",
 *   taskName: "TaskName"
 * }
 *
 * If project/task names don't exist in the provided lists, they're kept in the description
 */

/**
 * Parse mentions from description and extract project/task
 * @param {string} description - The full description with # mentions
 * @param {Array} projects - List of available projects
 * @param {Array} tasks - List of available tasks
 * @returns {Object} - { cleanDescription, project, task }
 */
export function parseMentions(description, projects = [], tasks = []) {
    if (!description || !description.trim()) {
        return {
            cleanDescription: '',
            project: null,
            task: null
        }
    }

    let cleanDescription = description
    let matchedProject = null
    let matchedTask = null

    // Find all potential project mentions (looking for # followed by project names)
    // We need to check for actual project names, not just simple hashtag patterns
    for (const project of projects) {
        const projectPattern = `#${project.name}`
        const projectIndex = cleanDescription.indexOf(projectPattern)

        if (projectIndex !== -1) {
            // Found a project mention - this is our matched project
            matchedProject = project

            // Remove the full project name mention (including the #)
            cleanDescription = cleanDescription.replace(projectPattern, '').trim()

            // Now look for a task mention for this project
            const projectTasks = tasks.filter(t => t.project_id === project.id)

            for (const task of projectTasks) {
                const taskPattern = `#${task.name}`
                const taskIndex = cleanDescription.indexOf(taskPattern)

                if (taskIndex !== -1) {
                    // Found a task mention
                    matchedTask = task

                    // Remove the full task name mention (including the #)
                    cleanDescription = cleanDescription.replace(taskPattern, '').trim()
                    break // Only match one task
                }
            }

            break // Only match one project
        }
    }

    // Clean up extra spaces
    cleanDescription = cleanDescription.replace(/\s+/g, ' ').trim()

    return {
        cleanDescription,
        project: matchedProject,
        task: matchedTask
    }
}

/**
 * Find best matching item based on name pattern (case-insensitive)
 * Priority: exact match > starts-with > contains
 */
function findMatchingItem(pattern, items) {
    if (!pattern || !items || items.length === 0) return null

    const lowerPattern = pattern.toLowerCase()

    // Try exact match first (case-insensitive)
    const exactMatch = items.find(item =>
        item.name.toLowerCase() === lowerPattern
    )
    if (exactMatch) return exactMatch

    // Try starts-with match
    const startsWithMatch = items.find(item =>
        item.name.toLowerCase().startsWith(lowerPattern)
    )
    if (startsWithMatch) return startsWithMatch

    // Try contains match
    const containsMatch = items.find(item =>
        item.name.toLowerCase().includes(lowerPattern)
    )

    return containsMatch
}

/**
 * Extract just the mention names from a description (for display purposes)
 * @param {string} description - The description with # mentions
 * @returns {Array} - Array of mention names
 */
export function extractMentionNames(description) {
    if (!description || !description.trim()) return []

    const mentionRegex = /#([^\s#]+)/g
    const mentions = []
    let match

    while ((match = mentionRegex.exec(description)) !== null) {
        mentions.push(match[1])
    }

    return mentions
}